import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import useQueryState from "../../../hooks/useQueryState";
import useAggregationLevel from "../../../hooks/useAggregationLevel";
import adminDivisionJson from "../../../assets/documents/admin-divisions.json";
import useAreaOfInterest from "../../../hooks/useAreaOfInterest";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { useEffect, useMemo, useState } from "react";
import DeleteAoi from "./partials/DeleteAoi";
import { useMatch } from "react-router-dom";

type AdminDivisionData = {
  [countryCode: string]: {
    admin1_term: string;
    admin2_term: string;
  };
};

const adminDivision: AdminDivisionData = adminDivisionJson;

const SelectAggregations = () => {
  const {
    queryState: { aggregationLevel, country },
    setQueryState,
  } = useQueryState();
  const {
    getAggregationLevel: { data: aggregationLevelData, isLoading },
  } = useAggregationLevel();
  const {
    getAreaOfInterests: { data: aoiData, isLoading: isLoadingAreaOfInterest },
  } = useAreaOfInterest();
  const handleChangeAggregationLevel = (event: SelectChangeEvent<string>) => {
    setQueryState({ aggregationLevel: event.target.value });
  };
  const { parsedToken } = useAuthContext();
  const { email: userEmail } = parsedToken || {};
  const [selectedAoi, setSelectedAoi] = useState<{ id: string; name: string }>({
    id: "",
    name: "",
  });
  const isGetStartedPage = useMatch("/dashboard/get-started");

  const adminDivisionLabel = (id: string) => {
    if (!country) return "";
    if (id === "1")
      return adminDivision?.[country]?.["admin1_term"] ?? "Admin 1";
    if (id === "2")
      return adminDivision?.[country]?.["admin2_term"] ?? "Admin 2";
    // Add more levels if needed
    return "";
  };

  useEffect(() => {
    if (!aggregationLevel) {
      setQueryState({
        aggregationLevel: userEmail
          ? (aoiData?.data?.sort((a, b) =>
              a.areaOfInterestName.localeCompare(b.areaOfInterestName),
            )[0]?.id ?? "")
          : (aggregationLevelData?.data
              .filter((each) => each?.id !== "0")
              .sort((a, b) => a.name.localeCompare(b.name))[0]?.id ?? ""),
      });
    }
  }, [country]);

  const finalAggregationData = useMemo(() => {
    const data: any[] = [];

    if (aggregationLevelData?.data) {
      data.push(
        ...aggregationLevelData.data
          .filter((each) => each?.id !== "0" && each.type === "system")
          .map((each) => ({
            ...each,
            name: adminDivisionLabel(each?.id) ?? each.name,
          })),
      );
    }

    if (aoiData?.data) {
      const adminLevel = aoiData.data.map((each) => ({
        ...each,
        uuid: each?.id,
        id: each.areaOfInterestName,
        name: each.areaOfInterestName,
        type: "user",
      }));
      data.push(...adminLevel);
    }

    return data.sort((a, b) => a?.name?.localeCompare(b?.name ?? "") ?? 0);
  }, [aggregationLevelData, aoiData]);

  useEffect(() => {
    if (
      finalAggregationData &&
      finalAggregationData.find((each) => each.name !== aggregationLevel)
    ) {
      const selectedAggregation = finalAggregationData.find((each) =>
        aggregationLevel
          ? aggregationLevel === each.id
          : each.status !== "process" && each.status !== "ready",
      );

      setQueryState({
        aggregationLevel:
          selectedAggregation?.id ?? finalAggregationData[0]?.id,
      });
    }
  }, [finalAggregationData]);

  const handleDeleteFile = async ({
    id,
    name,
  }: {
    id: string;
    name: string;
  }) => {
    setSelectedAoi({ id, name });
  };

  return (
    <FormControl fullWidth>
      <InputLabel id="select-aggregation-level">Aggregation level </InputLabel>
      <Select
        labelId="select-aggregation-level"
        id="select-aggregation-level"
        value={`${aggregationLevel}`}
        label="Aggregation level"
        onChange={handleChangeAggregationLevel}
        renderValue={(selected) => {
          const selectedItem = finalAggregationData.find(
            (item) => item?.id === selected,
          );

          return (
            <Typography variant="body1">{selectedItem?.name ?? ""}</Typography>
          );
        }}
      >
        {(isLoading || isLoadingAreaOfInterest) && (
          <Box
            width="100%"
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress size={20} />
          </Box>
        )}
        {finalAggregationData.length > 0 ? (
          finalAggregationData.map((aoi) => (
            <MenuItem
              key={`aggregation-${aoi?.id}`}
              value={aoi?.id}
              disabled={aoi?.status === "process" || aoi?.status === "ready"}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <Typography variant="body1">{aoi.name}</Typography>
                <Stack gap={0.5} direction="row">
                  {(aoi.status === "process" || aoi.status === "ready") && (
                    <Tooltip title="File is being uploaded…d">
                      <Chip label={"processing..."} size="small" />
                    </Tooltip>
                  )}
                  {aoi.status === "failed" && (
                    <Tooltip title="File failed to upload">
                      <Chip label={aoi.status} size="small" />
                    </Tooltip>
                  )}
                  {aoi.type === "user" &&
                    aoi.status !== "process" &&
                    isGetStartedPage && (
                      <IconButton
                        size="small"
                        sx={{ color: "#d32f2f", p: 0 }}
                        onClick={() =>
                          handleDeleteFile({ id: aoi.uuid, name: aoi.name })
                        }
                      >
                        <Tooltip title="Remove uploaded file">
                          <ClearIcon fontSize="small" />
                        </Tooltip>
                      </IconButton>
                    )}
                </Stack>
              </Stack>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>Please select a country</MenuItem>
        )}
      </Select>
      {Object.keys(selectedAoi).length > 0 && (
        <DeleteAoi selectedAoi={selectedAoi} setSelectedAoi={setSelectedAoi} />
      )}
    </FormControl>
  );
};

export default SelectAggregations;
